/**
 * SpatialOverlay — Reusable iOS 26 Spatial Layering push transition.
 *
 * Wraps any fullscreen content with the spatial layering effect:
 * the AppLayout shell shifts left (parallax), gets rounded corners + scrim,
 * while the overlay slides in from the right. Includes edge-swipe gesture
 * for going back.
 *
 * Animation logic delegated to useSpatialPush hook (shared with stackTransitionPlugin).
 *
 * Usage:
 *   <SpatialOverlay isActive={isMessages} shellRef={shellRef}>
 *     <MessagingLayout />
 *   </SpatialOverlay>
 */

import { useState, useRef, useEffect, type RefObject, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpatialPush } from '@/hooks/useSpatialPush';
import {
  ENABLE_SCRIM_BLUR,
  SCRIM_COLOR,
} from '@/utils/spatialTransition';

/**
 * Lock the shell to exactly viewport height so that `position: fixed`
 * children (TopBar at top:0, BottomNav at bottom:0) stay aligned with the
 * viewport edge even when the shell has a CSS transform (which turns it
 * into a containing block for fixed descendants).
 *
 * Also listens for visualViewport resize (e.g. virtual keyboard open/close)
 * so the locked height stays accurate.
 */
let viewportResizeCleanup: (() => void) | null = null;

function lockShellToViewport(el: HTMLElement) {
  // Clean up any previous listener (re-entrant safety)
  viewportResizeCleanup?.();
  viewportResizeCleanup = null;

  window.scrollTo(0, 0);
  const vp = window.visualViewport;
  el.style.height = `${vp?.height ?? window.innerHeight}px`;
  el.style.overflow = 'hidden';

  // Update shell height when virtual keyboard changes viewport size
  if (vp) {
    const onResize = () => {
      el.style.height = `${vp.height}px`;
    };
    vp.addEventListener('resize', onResize);
    viewportResizeCleanup = () => vp.removeEventListener('resize', onResize);
  }
}

function unlockShell(el: HTMLElement) {
  viewportResizeCleanup?.();
  viewportResizeCleanup = null;
  el.style.height = '';
  el.style.overflow = '';
}

interface SpatialOverlayProps {
  isActive: boolean;
  shellRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
}

export function SpatialOverlay({
  isActive,
  shellRef,
  children,
}: SpatialOverlayProps) {
  const navigate = useNavigate();
  const [shouldMount, setShouldMount] = useState(isActive);
  const overlayRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  // Set true by onAfterPop — prevents the mount trigger from re-mounting
  // the overlay while navigate('/messages') is still propagating (isActive
  // is stale true).  Cleared once isActive goes false.
  const unmountingRef = useRef(false);

  // ── Mount trigger ──
  // When isActive goes true: mount the overlay DOM so useSpatialPush can
  // animate it.  Unmounting is handled exclusively by onAfterPop calling
  // setShouldMount(false) — never by this effect.
  useEffect(() => {
    if (isActive) {
      // Don't re-mount after a gesture/programmatic pop — navigate is still
      // propagating and isActive will go false on the next render.
      if (unmountingRef.current) return;
      if (!shouldMount) setShouldMount(true);
    } else {
      unmountingRef.current = false;
    }
  }, [isActive, shouldMount]);

  useSpatialPush({
    parentRef: shellRef,
    childRef: overlayRef,
    scrimRef,
    isActive,
    mounted: shouldMount,
    onPop: () => navigate('/messages', { replace: true }),
    onBeforePush: (el) => lockShellToViewport(el),
    onAfterPop: (el) => {
      unlockShell(el);
      unmountingRef.current = true;
      setShouldMount(false);
    },
  });

  if (!shouldMount) return null;

  return (
    <>
      {/* Scrim: dark tint over shell */}
      <div
        ref={scrimRef}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 55,
          backgroundColor: SCRIM_COLOR,
          backdropFilter: ENABLE_SCRIM_BLUR ? 'blur(4px)' : undefined,
          WebkitBackdropFilter: ENABLE_SCRIM_BLUR ? 'blur(4px)' : undefined,
          pointerEvents: 'none',
          visibility: 'hidden',
        }}
      />

      {/* Overlay: contains the fullscreen content */}
      <div
        ref={overlayRef}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 60,
          background: '#F8FAFC',
          visibility: 'hidden',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </>
  );
}
